import PaymentVisitDetails from './PaymentVisitDetails';
import CollectPaymentSummary from './CollectPaymentSummary';
import {paymentMethodDetailsError} from './payment-method-details';
import { UNNAMED_CLINIC_LABEL } from '@/dashboard/clinic-context';
import ModalShell from '@/dashboard/ui/ModalShell';
import ReceiptPrintView from './ReceiptPrintView';
import PaymentMethodSelector from './PaymentMethodSelector';
import { collectionOutstandingMinorUnits } from './checkout-handoff-view';
import { BillCollectionReview, useBillCollectionReview } from './BillCollectionReview';
import { parseInrToMinorUnits } from './billing-entry-view';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import DrawerShell from '@/dashboard/ui/DrawerShell';
import { WorkPanel } from '@/dashboard/ui/MasterDetail';
import { type FieldGridField } from '@/dashboard/ui/FieldGrid';
import AcButton from '@/dashboard/ui/AcButton';
import { acLinkClass } from '@/dashboard/ui/AcLink';
import LoadingState from '@/dashboard/ui/LoadingState';
import ErrorState from '@/dashboard/ui/ErrorState';
import { fetchCheckoutHandoff } from '@/dashboard/api';
import { safeErrorMessage } from '@/lib/portal-api';
import { formatTimestampIst } from '@/lib/datetime-ist';
import {
  BillingReceiptPanel,
  CollectionRecoveryPanel,
  useCollectionRecovery,
  useCollectionMethodRestoration,
  type BillingReceipt,
} from '@/dashboard/billing/BillingEntryPage';
import {
  formatMinorUnitsInr,
  methodLabel,
  referenceNumberForWire,
  type PatientPaymentRow,
} from '@/dashboard/billing/billing-entry-view';
import {
  PAYMENT_EVIDENCE_LABELS,
  visitBillPaymentSummary,
  PRICE_UNAVAILABLE_COPY,
  type CheckoutHandoffResponse,
} from '@/dashboard/billing/checkout-handoff-view';
import {
  canSubmitPaymentDrawer,
  derivePaymentDrawerPrefill,
  fieldErrorFor,
  isCurrentArm,
  paymentDrawerActionAvailable,
  paymentDrawerUnavailableReason,
  paymentDrawerOutstanding,
  validatePaymentDrawerForm,
  type PaymentDrawerArm,
  type PaymentDrawerFieldError,
  type PaymentDrawerForm,
  type PaymentDrawerSubmitState,
} from '@/dashboard/billing/payment-drawer-view';
import type { DashboardMe } from '@/dashboard/types';

/**
 * SMART-PAYMENT-DRAWER-V1 -- THE in-place collect-payment drawer (Refs #315).
 *
 * ONE job: collect THIS visit's payment. Opened in place from a day-board row
 * or a billing-queue row; prefilled from the ONE existing checkout read;
 * recorded through the ONE existing writer; the receipt renders IN PLACE.
 * The normal path changes NO url, and /dashboard/billing-entry stays
 * behaviourally byte-unchanged for search, history, voids, unlinked payments,
 * split and complex cases -- Full Billing remains the deep-work surface, and
 * this drawer LINKS to it rather than replacing it.
 *
 * REUSE (re-deriving any is a package failure): DrawerShell hosts; FieldGrid
 * renders facts; AcButton is the ONE action primitive; StatusChip carries the
 * payment-evidence tone; LoadingState / ErrorState carry the lanes;
 * BillingReceiptPanel IS the receipt; the money parser + formatter are
 * billing-entry-view's; the writer is POST /api/patient-payments unchanged.
 *
 * INTENTIONALLY ABSENT (each a registered deferral with a named carrier, not
 * an oversight): voids (supervisory -- stays on the page), payment history,
 * split / multi-method tender (needs an atomic allocation model), free-text
 * note, category, incentive / margin data, and marketing fields.
 *
 * THE REFERENCE NUMBER IS NO LONGER ABSENT (L-03 / R-14, FRONT-DESK-MONEY
 * lane 2). The column, the writer and the wire already shipped
 * (BILLING-MISSING-DELTA-V1, migration 20260830_1 founder-applied
 * 2026-08-30); only this drawer could not type one, so a payment collected in
 * place was unreconcilable. It is OPTIONAL here, exactly as it is on Full
 * Billing -- no repo rule makes it mandatory for a non-cash mode, and a lane
 * does not mint one. Format validation stays SERVER-side (PAYMENT_REFERENCE_RE).
 *
 * The presentational halves below are EXPORTED for direct SSR pinning (the
 * house idiom -- this repo has no jsdom, so behaviour is proven against pure
 * renders plus the pure view module).
 */

export interface PaymentDrawerProps {
  /** The episode to collect for. V1 is VISIT-ANCHORED ONLY -- required. */
  readonly appointmentId: number;
  /** Presentation only; day-board host owns the adjacent detail column. */
  readonly inPlane?: boolean;
  readonly onClose: () => void;
  /** Fired after a payment commits so the host can refresh its rows. */
  readonly onRecorded?: () => void;
  /** The Full Billing deep-work door (history, voids, split, complex cases). */
  readonly fullBillingHref: string;
  /** Passed straight through to the shared receipt. */
  readonly dayBoardPath?: string | null;
  readonly me?: DashboardMe | null;
}

/** The honest fallback when a stored timestamp will not parse. */
export const PAYMENT_DRAWER_TIME_UNAVAILABLE_COPY = 'Time unavailable';
/** Shown in place of the form when the caller may not record here. */
export const PAYMENT_DRAWER_NO_ACTION_COPY =
  'Recording is not available for your role here.';
/** The deep-work door out of the drawer. */
/** Home/visit collection uses authoritative due; this is not Full Billing's advance entry. */
export function visitCollectionUnavailableReason(handoff: CheckoutHandoffResponse): string | null {
  const refusal = paymentDrawerUnavailableReason(handoff);
  if (refusal) return refusal;
  const due = collectionOutstandingMinorUnits(handoff);
  if (due === null || !Number.isSafeInteger(due) || due < 0) return 'Amount due is unavailable. Check the saved bill before collecting.';
  return due === 0 ? 'Nothing is due on this visit.' : null;
}
export function visitCollectionActionAvailable(handoff: CheckoutHandoffResponse): boolean {
  return paymentDrawerActionAvailable(handoff) && visitCollectionUnavailableReason(handoff) === null;
}
/** Advisory field feedback follows the existing server reference-token contract.
 * Server validation remains authoritative; blank is still allowed. */
export function collectionReferenceError(raw: string): string | null {
  const value = raw.trim();
  return value === '' || /^[A-Za-z0-9][A-Za-z0-9/._-]{0,63}$/.test(value)
    ? null : 'Use up to 64 letters, digits, / . _ or -.';
}

export const PAYMENT_DRAWER_FULL_BILLING_COPY = 'Open full view';

/** Local-input value for "now" (the billing page's idiom). */
function nowLocalInputValue(): string {
  const d = new Date();
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

/** IST-labelled appointment time (zone-labelled per the render law). */
function formatIst(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return PAYMENT_DRAWER_TIME_UNAVAILABLE_COPY;
  return formatTimestampIst(iso);
}

/**
 * The header facts: SHORT LABELED VALUES, never prose, never recomputed.
 * Exported so the field set itself is pinnable.
 *
 * UMR is OMITTED ENTIRELY when the wire carries none -- flag off, no patient
 * link, or a role that may not record here. An honest omission, never an
 * empty label, and never a blocker on the payment.
 */
export function paymentDrawerHeaderFields(
  handoff: CheckoutHandoffResponse,
): readonly FieldGridField[] {
  const fields: FieldGridField[] = [
    {
      label: 'Patient',
      value: handoff.patientLabel,
      missingLabel: 'Not recorded',
      testId: 'payment-drawer-patient',
    },
  ];
  if (typeof handoff.patientUmr === 'string' && handoff.patientUmr !== '') {
    fields.push({
      label: 'UMR',
      value: handoff.patientUmr,
      testId: 'payment-drawer-umr',
    });
  }
  fields.push(
    {
      label: 'Clinic',
      value: handoff.clinicLabel ?? null,
      missingLabel: 'Not recorded',
      testId: 'payment-drawer-clinic',
    },
    {
      label: 'Visit',
      value: `#${handoff.doorAppointmentId} - ${formatIst(
        handoff.appointmentTimeIso,
      )}`,
      testId: 'payment-drawer-visit',
    },
    {
      // The episode-grain word, never the forbidden account-grain
      // "Amount due" / "Balance due" (gap.patient-account-model).
      label: (handoff.finalizedBills?.billCount ?? 0)>0 ? 'Bill balance' : 'Treatment estimate remaining',
      value: paymentDrawerOutstanding(handoff).text,
      testId: 'payment-drawer-due',
    },
  );
  return fields;
}

/** The money evidence: the SERVER's numbers, displayed, never netted here. */
export function paymentDrawerMoneyFields(
  handoff: CheckoutHandoffResponse,
): readonly FieldGridField[] {
  const fields: FieldGridField[] = [
    {
      label: 'Accepted',
      value:
        handoff.acceptedValuedSumInr !== null
          ? formatMinorUnitsInr(handoff.acceptedValuedSumInr * 100)
          : null,
      missingLabel: PRICE_UNAVAILABLE_COPY,
      testId: 'payment-drawer-accepted',
    },
    {
      // Nothing collected yet is a STATE, rendered in the wire's own
      // vocabulary -- this surface never prints a zero money figure (the
      // billing queue's same convention).
      label: 'Received on this visit',
      value:
        handoff.collectedMinorUnits > 0
          ? formatMinorUnitsInr(handoff.collectedMinorUnits)
          : null,
      missingLabel: PAYMENT_EVIDENCE_LABELS.none_recorded,
      testId: 'payment-drawer-collected',
    },
  ];
  fields.push({label: 'Applied to bills on this visit', value: visitBillPaymentSummary(handoff), testId: 'payment-drawer-bill-paid'});
  // RECEPTION-MONEY-TRUTH-V1 (A2): the prior-money facts the desk asked
  // for at collect time -- the SERVER's patient-grain numbers, displayed
  // beside the episode's own (never netted here; a second derivation is a
  // package failure). ABSENT wire block = NO rows (honest omission).
  const money = handoff.patientMoney ?? null;
  if (money !== null) {
    if (money.lastPayment !== null) {
      fields.push({
        label: money.scopeClinicCount === 1 ? 'Patient receipts · 1 permitted clinic' : money.scopeClinicCount ? `Patient receipts · ${money.scopeClinicCount} permitted clinics` : 'Patient receipts · permitted clinics',
        value: `${money.historyCapped ? 'Partial history · ' : ''}${formatMinorUnitsInr(money.paidToDateMinorUnits)} (last ${formatMinorUnitsInr(
          money.lastPayment.amountMinorUnits,
        )}${money.lastPayment.method !== null ? ` ${methodLabel(money.lastPayment.method)}` : ''} - ${formatIst(
          money.lastPayment.occurredAtIso,
        )})`,
        testId: 'payment-drawer-paid-before',
      });
    }
    if (money.carriedMinorUnits > 0) {
      fields.push({
        label: 'Balance carried',
        value: `${formatMinorUnitsInr(money.carriedMinorUnits)} from earlier visits${
          money.carriedNotConfiguredCount > 0
            ? ` (+${money.carriedNotConfiguredCount} unresolved balance${money.carriedNotConfiguredCount === 1 ? '' : 's'})`
            : ''
        }`,
        testId: 'payment-drawer-carried',
      });
    }
  }
  return fields;
}

/** RECEPTION-MONEY-TRUTH-V1 (A3): the LOUD no-estimate flag. A collect door
 *  with NO amount must never be silent about why -- the desk confirms the
 *  amount with the doctor instead of guessing (the C6 fee-missing class,
 *  carried to the desk surface; the NULL-AMOUNT LAW's render half). */
export const PAYMENT_DRAWER_NO_ESTIMATE_COPY =
  'No fee estimate on this visit - confirm the amount with the doctor before collecting.';

export function paymentDrawerNoEstimateWarning(
  handoff: CheckoutHandoffResponse,
): string | null {
  if((handoff.finalizedBills?.billCount ?? 0)>0)return null;
  const collectable =
    handoff.queueState === 'outstanding' || handoff.queueState === 'not_configured';
  if (!collectable) return null;
  return handoff.doorSuggestedAmountInr === null &&
    handoff.acceptedValuedSumInr === null
    ? PAYMENT_DRAWER_NO_ESTIMATE_COPY
    : null;
}

/**
 * The ROLE-PROJECTED plan lines. The server already carved this by audience
 * (projectCaseSheetForRole -- the billing audience receives procedure + value
 * + status ONLY; clinical narrative is structurally ABSENT on the wire, and
 * the manager audience ships no per-item rows at all). This render therefore
 * shows whatever the caller's OWN projection lawfully carried, and invents
 * nothing: an absent planItems array renders NO section.
 */
export function paymentDrawerPlanFields(
  handoff: CheckoutHandoffResponse,
): readonly FieldGridField[] {
  const items = handoff.plan?.planItems ?? [];
  return items.map((item) => ({
    label: item.procedureName,
    value:
      item.estimatedValue !== null
        ? formatMinorUnitsInr(item.estimatedValue * 100)
        : null,
    missingLabel: PRICE_UNAVAILABLE_COPY,
    testId: `payment-drawer-plan-${item.id}`,
  }));
}

// ---- The action strip -----------------------------------------------------

export interface PaymentDrawerFooterProps {
  readonly inPlane?: boolean;
  readonly recoveryReady?: boolean;
  readonly amountLabel?: string;
  readonly methodReady?: boolean;
  readonly handoff: CheckoutHandoffResponse;
  readonly submitState: PaymentDrawerSubmitState;
  /** TRUE once the receipt has replaced the form (terminal). */
  readonly recorded: boolean;
  readonly fullBillingHref: string;
  readonly onSubmit: () => void;
}

/**
 * EXACTLY ONE primary action. When the server says the caller may not record
 * here, the action is STRUCTURALLY ABSENT -- never rendered-but-disabled,
 * never a button that would 401. After success only the deep-work link
 * remains, so the strip can never look ready-to-resubmit.
 */
export function PaymentDrawerFooter({
  inPlane = false,
  recoveryReady = true,
  amountLabel,
  methodReady = true,
  handoff,
  submitState,
  recorded,
  fullBillingHref,
  onSubmit,
}: PaymentDrawerFooterProps): JSX.Element {
  const fullBilling = (
    <Link
      to={inPlane ? `/dashboard/billing-entry?clinicId=${handoff.clinicId}#billing-recent` : fullBillingHref}
      className={acLinkClass('brand', 'payment-drawer-history-link')}
      data-testid="payment-drawer-full-billing"
    >
      {inPlane ? 'Payment history' : PAYMENT_DRAWER_FULL_BILLING_COPY}
    </Link>
  );
  if (recorded) return fullBilling;
  return (
    <div className={inPlane ? "ac-payment-panel__actions" : undefined}>
      {inPlane ? fullBilling : null}
      {visitCollectionActionAvailable(handoff) ? (
        <AcButton
          variant="primary"
          size="sm"
          onClick={onSubmit}
          disabled={submitState === 'submitting' || !recoveryReady || !methodReady}
          data-testid="payment-drawer-submit"
        >
          {submitState === 'submitting' ? 'Recording' : `Record payment${amountLabel ? ` · ${amountLabel}` : ''}`}
        </AcButton>
      ) : null}
      {!inPlane ? fullBilling : null}
    </div>
  );
}

// ---- The drawer body ------------------------------------------------------

export interface PaymentDrawerBodyProps {
  readonly allocation?: ReactNode;
  readonly detailScopeKey?: string;
  readonly onOpenReceipt?: () => void;
  readonly inPlane?: boolean;
  readonly handoff: CheckoutHandoffResponse;
  readonly form: PaymentDrawerForm;
  readonly errors: readonly PaymentDrawerFieldError[];
  readonly submitState: PaymentDrawerSubmitState;
  readonly submitError: string | null;
  /** Non-null REPLACES the form with the shared receipt. */
  readonly receipt: BillingReceipt | null;
  readonly onChange: (patch: Partial<PaymentDrawerForm>) => void;
  readonly dayBoardPath?: string | null;
  readonly me?: DashboardMe | null;
}

export function PaymentDrawerBody({
  detailScopeKey,
  allocation,
  onOpenReceipt,
  inPlane = false,
  handoff,
  form,
  errors,
  submitState,
  submitError,
  receipt,
  onChange,
  dayBoardPath = null,
  me = null,
}: PaymentDrawerBodyProps): JSX.Element {
  const [detailsOpen,setDetailsOpen]=useState(false);
  // THE RECEIPT REPLACES THE FORM. Not "in addition to" -- the editable form
  // is gone, so the surface can never look still-ready-to-submit, and an
  // idempotent replay renders its own "Already recorded" heading.
  if (receipt !== null) {
    return (
      <div className={inPlane ? "ac-collect__receipt" : undefined}><BillingReceiptPanel
        receipt={receipt}
        compact={inPlane}
        onPrintReceipt={onOpenReceipt}
        receiptActionLabel="View receipt"
        // A drawer is bound to ONE episode: "Record another payment" is
        // omitted, and the anchor to the page's recent-payments heading is
        // omitted too -- that heading does not exist over the day board, and
        // an anchor to a missing target is a DEAD DOOR (LES-016). The
        // footer's Open full view link retains both. In-plane Close already
        // returns to this board, so do not render another board-navigation exit.
        recentPaymentsHref={null}
        dayBoardPath={inPlane ? null : dayBoardPath}
        viewerRole={me?.role ?? null}
        me={me}
      /></div>
    );
  }

  const amountError = fieldErrorFor(errors, 'amount');
  const methodError = fieldErrorFor(errors, 'method');
  const referenceError = collectionReferenceError(form.referenceRaw);
  const submitting = submitState === 'submitting';

  return (
    <div className={inPlane ? "ac-payment-panel" : undefined}>
      <CollectPaymentSummary handoff={handoff} patientName={handoff.patientLabel ?? 'Patient'} patientUmr={handoff.patientUmr ?? null} clinicLabel={handoff.clinicLabel ?? UNNAMED_CLINIC_LABEL} fromDayBoard={false}/>
      {!visitCollectionActionAvailable(handoff) ? (
        handoff.canRecordPayment && handoff.acceptedValuedSumInr === null && handoff.outstandingMinorUnits === null && (handoff.finalizedBills?.billCount ?? 0) === 0 ? null : <p data-testid="payment-drawer-no-action">
          {visitCollectionUnavailableReason(handoff)}
        </p>
      ) : (
        // The billing page's OWN styled form idiom, reused -- a new
        // drawer-local class would ship unstyled (no payment-drawer rule
        // exists in dashboard.css) and would be a second form vocabulary.
        <fieldset className="billing-entry__group" data-testid="payment-drawer-form">
          <legend className="ac-sr-only">Payment</legend>
          {/* RECEPTION-MONEY-TRUTH-V1 (A3): a collect door with NO fee
              estimate is flagged LOUDLY, never silent -- the desk confirms
              with the doctor instead of guessing (the C6 fee-missing class
              at the desk; the NULL-AMOUNT LAW's render half). role=alert:
              this is a warning the desk must read before typing an amount. */}
          {paymentDrawerNoEstimateWarning(handoff) !== null ? (
            <p
              className="billing-entry__warning"
              role="alert"
              data-testid="payment-drawer-no-estimate"
            >
              {paymentDrawerNoEstimateWarning(handoff)}
            </p>
          ) : null}
          <label htmlFor="payment-drawer-amount">
            Amount (₹) *
          </label>
          <input
            id="payment-drawer-amount"
            type="text"
            inputMode="decimal"
            value={form.amountRaw}
            disabled={submitting}
            aria-invalid={amountError !== null}
            aria-describedby={
              amountError !== null ? 'payment-drawer-amount-error' : undefined
            }
            data-testid="payment-drawer-amount"
            onChange={(e) => onChange({ amountRaw: e.target.value })}
          />
          {amountError !== null ? (
            <p
              id="payment-drawer-amount-error"
              className="ac-wizard__field-error"
              role="alert"
              data-testid="payment-drawer-amount-error"
            >
              <span className="ac-wizard__field-error-glyph" aria-hidden="true">
                !
              </span>
              {amountError}
            </p>
          ) : null}

          <PaymentMethodSelector referenceRaw={form.referenceRaw} referenceTestId="payment-drawer-reference" referenceError={referenceError} onReferenceChange={value=>onChange({referenceRaw:value})} method={form.method} methodSubtype={form.methodSubtype??''} methodOtherName={form.methodOtherName??''} disabled={submitting} onDetailsChange={onChange} onChange={method=>onChange({method,...(method==='cash'?{referenceRaw:''}:{})})}/>
          {methodError !== null ? (
            <p
              id="payment-drawer-method-error"
              className="ac-wizard__field-error"
              role="alert"
              data-testid="payment-drawer-method-error"
            >
              <span className="ac-wizard__field-error-glyph" aria-hidden="true">
                !
              </span>
              {methodError}
            </p>
          ) : null}



          {/* A server failure keeps every entered value and explains itself
              here -- the form is intact and retryable with the SAME
              requestId, so a retry can never mint a second payment row. */}
          {submitError !== null && !inPlane ? (
            <p
              className="ac-banner ac-banner--danger"
              role="alert"
              data-testid="payment-drawer-submit-error"
            >
              {submitError}
            </p>
          ) : null}
        </fieldset>
      )}
      {allocation}
      <details className="quiet-payment-details" onToggle={e=>setDetailsOpen(e.currentTarget.open)}><summary>Treatment, bills &amp; history</summary><PaymentVisitDetails handoff={handoff} role={me?.role} active={detailsOpen} scopeKey={detailScopeKey}/></details>
    </div>
  );
}

// ---- The container --------------------------------------------------------

export default function PaymentDrawer({
  appointmentId,
  inPlane = false,
  onClose,
  onRecorded,
  fullBillingHref,
  dayBoardPath = null,
  me = null,
}: PaymentDrawerProps): JSX.Element {
  const collectionRecovery = useCollectionRecovery(me);
  const [handoff, setHandoff] = useState<CheckoutHandoffResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [form, setForm] = useState<PaymentDrawerForm>({
    amountRaw: '',
    method: '',
    receivedAtLocal: nowLocalInputValue(),
    referenceRaw: '',
  });
  const billCollectionReview=useBillCollectionReview(handoff?.clinicId??null,handoff?.patientId??null,parseInrToMinorUnits(form.amountRaw),JSON.stringify([form.method,form.methodSubtype,form.methodOtherName,form.referenceRaw,appointmentId]),true);
  const [errors, setErrors] = useState<readonly PaymentDrawerFieldError[]>([]);
  const [submitState, setSubmitState] =
    useState<PaymentDrawerSubmitState>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<BillingReceipt | null>(null);
  const [receiptOpen,setReceiptOpen] = useState(false);
  const [receiptDocumentTitle,setReceiptDocumentTitle]=useState('Payment receipt');
  const armRef = useRef<PaymentDrawerArm | null>(null);
  const seqRef = useRef(0);

  // ONE handoff read per arming. A fresh requestId is minted AT ARM and reused
  // for every retry of this submission; the stale-arm token makes a response
  // for visit A structurally unable to populate visit B's drawer.
  useEffect(() => {
    seqRef.current += 1;
    const armToken = seqRef.current;
    armRef.current = null;
    setHandoff(null);
    setLoadError(null);
    setReceipt(null);
    setReceiptOpen(false);
    setErrors([]);
    setSubmitError(null);
    setSubmitState('idle');
    fetchCheckoutHandoff(appointmentId)
      .then((res) => {
        if (seqRef.current !== armToken) return; // stale arming
        armRef.current = {
          // LES-022: the row's OWN episode, verbatim -- never re-picked.
          appointmentId: res.doorAppointmentId,
          clinicId: res.clinicId,
          patientId: res.patientId,
          requestId: crypto.randomUUID(),
          seq: armToken,
        };
        setHandoff(res);
        setForm({...derivePaymentDrawerPrefill(res, nowLocalInputValue()).form, amountRaw:''});
      })
      .catch((err: unknown) => {
        if (seqRef.current !== armToken) return; // stale arming
        setLoadError(safeErrorMessage(err, 'Could not load this visit.'));
      });
  }, [appointmentId]);

  const methodContext = handoff ? JSON.stringify([handoff.clinicId, handoff.patientId, appointmentId]) : null;
  const saveMethodChoice = collectionRecovery.saveMethodChoice;
  const restoreMethod = useCallback((method: string) => {
    setForm(f => ({...f, method, referenceRaw:''}));
  }, []);
  const markMethodEdited = useCollectionMethodRestoration(collectionRecovery, methodContext, restoreMethod);

  const onChange = useCallback((patch: Partial<PaymentDrawerForm>) => {
    if (patch.method !== undefined || patch.referenceRaw !== undefined) markMethodEdited();
    setForm((f) => ({ ...f, ...patch, ...(patch.method === 'cash' ? {referenceRaw:''} : {}) }));
    if (patch.method !== undefined && methodContext) saveMethodChoice(methodContext, patch.method);
  }, [methodContext, saveMethodChoice, markMethodEdited]);

  const onSubmit = useCallback(() => {
    const arm = armRef.current;
    if (arm === null || handoff === null) return;
    // The GUARD, not the button: a double-submit is refused even if the
    // action is driven programmatically.
    if (!canSubmitPaymentDrawer(submitState)) return;
    if (!visitCollectionActionAvailable(handoff) || arm.patientId === null) return;

    const check = validatePaymentDrawerForm({
      ...form,
      // This legacy validation shape is internal; operators cannot edit time.
      receivedAtLocal: nowLocalInputValue(),
    });
    setErrors(check.errors);
    setSubmitError(null);
    if (
      collectionReferenceError(form.referenceRaw) !== null ||
      !check.ok ||
      check.amountMinorUnits === null ||
      check.receivedAtIso === null
    ) {
      setSubmitState('idle');
      return;
    }

    if(!billCollectionReview.command){setSubmitError(inPlane?'Bill allocation is not ready. Wait for it to finish or retry.':'Review the bill allocation before recording.');return;}
    setSubmitState('submitting');
    collectionRecovery.submit({
      ...billCollectionReview.command,
      clinicId: arm.clinicId,
      patientId: arm.patientId,
      // VISIT-ANCHORED: the armed episode, never a re-pick.
      appointmentId: arm.appointmentId,
      amountMinorUnits: check.amountMinorUnits,
      method: form.method,
      // THE ONE reference-for-the-wire rule (billing-entry-view): a blank
      // field is null, never '' -- the same shape Full Billing sends.
      referenceNumber: form.method === 'cash' ? null : referenceNumberForWire(form.referenceRaw),
      methodSubtype:form.methodSubtype||null,methodOtherName:form.methodOtherName?.trim()||null,
      occurredAt: new Date(form.receivedAtLocal).toISOString(),
      // Reused across retries -> UNIQUE(organization_id, request_id) collapses
      // a double-submit to ONE row and ONE audit row.
      requestId: arm.requestId,
    })
      .then((res) => {
        if (!res) return;
        if (!isCurrentArm(armRef.current, arm.seq)) return; // stale response
        if (methodContext) saveMethodChoice(methodContext, '');
        setSubmitState('recorded');
        setSubmitError(null);
        setReceipt({
          payment: res.payment as PatientPaymentRow,
          created: res.created,
        });
        if (onRecorded) onRecorded();
      })
      .catch((err: unknown) => {
        if (!isCurrentArm(armRef.current, arm.seq)) return; // stale response
        // Entered data is PRESERVED on every failure, server errors included.
        setSubmitState('error');
        setSubmitError(
          safeErrorMessage(err, 'Payment is unconfirmed. Resume the same request.'),
        );
      });
  }, [form, handoff, onRecorded, submitState, collectionRecovery, billCollectionReview, methodContext, saveMethodChoice]);

  const submitting = submitState === 'submitting';
  const Panel = inPlane ? WorkPanel : DrawerShell;

  return (
    <>
    <Panel
      eyebrow={undefined}
      title="Collect payment"
      onClose={onClose}
      closeDisabled={submitting}
      closeOnScrim={!submitting}
      closeOnEscape={!submitting}
      {...(inPlane ? { notice: submitError !== null ? <p className="ac-banner ac-banner--danger" role="alert" data-testid="payment-drawer-submit-error">{submitError}</p> : null } : {})}
      footer={
        handoff !== null && !collectionRecovery.pending ? (
          <PaymentDrawerFooter
            inPlane={inPlane}
            methodReady={paymentMethodDetailsError({method:form.method,methodSubtype:form.methodSubtype||null,methodOtherName:form.methodOtherName?.trim()||null,referenceNumber:form.method==='cash'?null:referenceNumberForWire(form.referenceRaw)})===null}
            amountLabel={parseInrToMinorUnits(form.amountRaw) === null ? undefined : formatMinorUnitsInr(parseInrToMinorUnits(form.amountRaw)!)}
            recoveryReady={collectionRecovery.ready && billCollectionReview.command!==null}
            handoff={handoff}
            submitState={submitState}
            recorded={receipt !== null}
            fullBillingHref={fullBillingHref}
            onSubmit={onSubmit}
          />
        ) : undefined
      }
      testId="payment-drawer"
    >
      <CollectionRecoveryPanel recovery={collectionRecovery}
        clinic={handoff === null ? null : { clinicId: handoff.clinicId, clinicLabel: handoff.clinicLabel ?? null }}
        contextMatches={handoff !== null && collectionRecovery.pending?.clinicId === handoff.clinicId &&
          collectionRecovery.pending?.patientId === handoff.patientId &&
          collectionRecovery.pending?.appointmentId === handoff.doorAppointmentId}
        onRecovered={res => {
        setSubmitError(null); setSubmitState('recorded'); setReceipt({ payment: res.payment, created: res.created });
        onRecorded?.();
      }} />
      {collectionRecovery.pending ? null : loadError !== null ? (
        <ErrorState
          title="Could not load this visit"
          detail={loadError}
          testId="payment-drawer-error"
        />
      ) : handoff === null ? (
        <LoadingState label="Loading visit" testId="payment-drawer-loading" />
      ) : (
        <PaymentDrawerBody
          detailScopeKey={collectionRecovery.draftScope}
          onOpenReceipt={()=>setReceiptOpen(true)}
          allocation={!receipt ? <BillCollectionReview review={billCollectionReview}/> : undefined}
          inPlane={inPlane}
          handoff={handoff}
          form={form}
          errors={errors}
          submitState={submitState}
          submitError={submitError}
          receipt={receipt}
          onChange={onChange}
          dayBoardPath={dayBoardPath}
          me={me}
        />
      )}

    </Panel>
    {receiptOpen && receipt ? <ModalShell size="lg" trapFocus childHeaderActions title={receiptDocumentTitle} onClose={()=>setReceiptOpen(false)} testId="saved-receipt-panel"><ReceiptPrintView onDocumentTitleChange={setReceiptDocumentTitle} payment={receipt.payment} clinicLabel={handoff?.clinicLabel ?? UNNAMED_CLINIC_LABEL} backLabel="Back to payment" onBack={()=>setReceiptOpen(false)}/></ModalShell> : null}
    </>
  );
}
